/**
 * Org Settings → Teams. Manages the org's teams and their member
 * lists (#526 PR 5b).
 *
 * A team is an org-scoped grouping of users. Granting a team into a
 * workspace (via the WorkspaceMembers page) gives every team member
 * the granted role on that workspace. Per ADR 0010.
 *
 * Gates:
 *   - List teams: any org member (server: `teams.list = ALL_ROLES`)
 *   - Create / rename / delete team: org Owner/Admin
 *     (server: `teams.create / update / delete`)
 *   - Add / remove team members: org Owner/Admin
 *     (server: `teamMembers.add / remove`)
 *
 * The page surfaces read-only views for non-Admin members so they can
 * see what teams exist and who's in them, without affordances they
 * can't use. Mutation buttons only render when `canManage` is true.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button, Input, Label, Card, CardContent } from '@/components/ui'
import type { OrgWithRole, Team, TeamMember, UserDisplayMap } from '@/lib/api'
import { teamsAPI } from '@/lib/api'
import { isUserResolved, userDisplayName, userDisplaySecondary } from '@/lib/userDisplay'
import { PersonPicker } from '@/components/PersonPicker'
import { useCanI } from '@/hooks/useCanI'
import { useConfirm } from '@/hooks/useConfirm'

export function OrgTeams({ org }: { org: OrgWithRole }) {
  const canManage = useCanI('teams.create', org._id)
  // #1148: shared themed confirm, replacing native confirm().
  const confirm = useConfirm()

  const [teams, setTeams] = useState<Team[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setLoadError(null)
      const list = await teamsAPI.list(org._id)
      setTeams(list)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err))
    }
  }, [org._id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleDelete(team: Team) {
    const ok = await confirm({
      title: `Delete team "${team.name}"?`,
      description:
        'Members keep their org accounts, but every team grant on every workspace is removed.',
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!ok) return
    setActionError(null)
    setBusy(true)
    try {
      await teamsAPI.delete(org._id, team._id)
      await refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">Failed to load teams: {loadError}</p>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>Retry</Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Teams</h3>
          <p className="text-sm text-muted-foreground">
            Org-scoped user groupings. Grant a team into a workspace from the workspace's Members tab.
          </p>
        </div>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setShowCreate((s) => !s)}>
            {showCreate ? 'Cancel' : '+ New team'}
          </Button>
        )}
      </div>

      {showCreate && canManage && (
        <CreateTeamForm
          orgId={org._id}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false)
            await refresh()
          }}
        />
      )}

      {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}

      {teams.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No teams yet.
            {canManage && ' Click "+ New team" to create one.'}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {teams.map((team) => (
          <TeamCard
            key={team._id}
            org={org}
            team={team}
            canManage={canManage}
            isEditing={editingTeamId === team._id}
            onStartEdit={() => setEditingTeamId(team._id)}
            onStopEdit={() => setEditingTeamId(null)}
            onChanged={refresh}
            onDelete={() => void handleDelete(team)}
            busy={busy}
          />
        ))}
      </div>
    </div>
  )
}

function TeamCard({
  org,
  team,
  canManage,
  isEditing,
  onStartEdit,
  onStopEdit,
  onChanged,
  onDelete,
  busy,
}: {
  org: OrgWithRole
  team: Team
  canManage: boolean
  isEditing: boolean
  onStartEdit: () => void
  onStopEdit: () => void
  onChanged: () => Promise<void>
  onDelete: () => void
  busy: boolean
}) {
  // #1148: shared themed confirm, replacing native confirm().
  const confirm = useConfirm()
  const [members, setMembers] = useState<TeamMember[]>([])
  // #2094: member rows carry only a userId, so the names come
  // alongside them. Fail-soft — an unresolved id still renders.
  const [memberUsers, setMemberUsers] = useState<UserDisplayMap>({})
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [memberError, setMemberError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  async function loadMembers() {
    try {
      setMemberError(null)
      const { members: list, users } = await teamsAPI.listMembers(org._id, team._id)
      setMembers(list)
      setMemberUsers(users ?? {})
      setMembersLoaded(true)
    } catch (err) {
      setMemberError(err instanceof Error ? err.message : String(err))
    }
  }

  function toggleExpanded() {
    const next = !expanded
    setExpanded(next)
    if (next && !membersLoaded) void loadMembers()
  }

  async function handleAddMember(userId: string) {
    await teamsAPI.addMember(org._id, team._id, userId)
    await loadMembers()
  }

  async function handleRemoveMember(userId: string) {
    const ok = await confirm({
      title: `Remove ${userDisplayName(memberUsers, userId)} from team "${team.name}"?`,
      description: 'They lose every workspace grant this team conferred, but keep their org account.',
      confirmLabel: 'Remove',
      destructive: true,
    })
    if (!ok) return
    try {
      await teamsAPI.removeMember(org._id, team._id, userId)
      await loadMembers()
    } catch (err) {
      setMemberError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {isEditing ? (
          <EditTeamForm
            orgId={org._id}
            team={team}
            onCancel={onStopEdit}
            onSaved={async () => {
              onStopEdit()
              await onChanged()
            }}
          />
        ) : (
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-medium">{team.name}</div>
              {team.description && (
                <div className="text-sm text-muted-foreground">{team.description}</div>
              )}
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground mt-1"
                onClick={toggleExpanded}
              >
                {expanded ? '▾' : '▸'} {membersLoaded ? `${members.length} member(s)` : 'members'}
              </button>
            </div>
            {canManage && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={busy} onClick={onStartEdit}>Edit</Button>
                <Button variant="destructive" size="sm" disabled={busy} onClick={onDelete}>Delete</Button>
              </div>
            )}
          </div>
        )}

        {expanded && (
          <div className="border-t pt-3 space-y-2">
            {memberError && <p className="text-sm text-destructive" role="alert">{memberError}</p>}
            {membersLoaded && members.length === 0 && (
              <p className="text-sm text-muted-foreground">No members in this team yet.</p>
            )}
            {members.length > 0 && (
              <ul className="space-y-1">
                {members.map((m) => (
                  <li key={m._id} className="flex items-center justify-between text-sm">
                    <span>
                      <span
                        className={isUserResolved(memberUsers, m.userId) ? '' : 'font-mono text-xs'}
                        title={isUserResolved(memberUsers, m.userId) ? m.userId : undefined}
                      >
                        {userDisplayName(memberUsers, m.userId)}
                      </span>
                      {userDisplaySecondary(memberUsers, m.userId) && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {userDisplaySecondary(memberUsers, m.userId)}
                        </span>
                      )}
                    </span>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleRemoveMember(m.userId)}
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canManage && (
              <AddMemberForm
                orgId={org._id}
                existingUserIds={members.map((m) => m.userId)}
                onAdd={handleAddMember}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function CreateTeamForm({
  orgId,
  onCancel,
  onCreated,
}: {
  orgId: string
  onCancel: () => void
  onCreated: () => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Name is required.')
      return
    }
    setBusy(true)
    try {
      await teamsAPI.create(orgId, {
        name: trimmedName,
        description: description.trim() || undefined,
      })
      setName('')
      setDescription('')
      await onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded border p-4 space-y-3">
      <div>
        <Label htmlFor="team-name">Name</Label>
        <Input
          id="team-name"
          placeholder="e.g. QA Pool"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="team-desc">Description (optional)</Label>
        <Input
          id="team-desc"
          placeholder="What this team is for"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>Create</Button>
        <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </form>
  )
}

function EditTeamForm({
  orgId,
  team,
  onCancel,
  onSaved,
}: {
  orgId: string
  team: Team
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const [name, setName] = useState(team.name)
  const [description, setDescription] = useState(team.description ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Name is required.')
      return
    }
    const patch: { name?: string; description?: string } = {}
    if (trimmedName !== team.name) patch.name = trimmedName
    const trimmedDesc = description.trim()
    if (trimmedDesc !== (team.description ?? '')) patch.description = trimmedDesc
    if (Object.keys(patch).length === 0) {
      onCancel()
      return
    }
    setBusy(true)
    try {
      await teamsAPI.update(orgId, team._id, patch)
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <Label htmlFor={`team-${team._id}-name`}>Name</Label>
        <Input
          id={`team-${team._id}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor={`team-${team._id}-desc`}>Description</Label>
        <Input
          id={`team-${team._id}-desc`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>Save</Button>
        <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </form>
  )
}

function AddMemberForm({
  orgId,
  existingUserIds,
  onAdd,
}: {
  orgId: string
  existingUserIds: readonly string[]
  onAdd: (userId: string) => Promise<void>
}) {
  // #2095: searches the org's members by name or email. This was a
  // free-text user-id box, with a note waiting on a lookup endpoint
  // that was never built — the org's existing member list already
  // carries everything the picker needs, and every role that can add a
  // team member may already read it.
  const [userId, setUserId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = userId.trim()
    if (!trimmed) {
      setError('Pick a person first.')
      return
    }
    setBusy(true)
    try {
      await onAdd(trimmed)
      setUserId('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2 pt-2">
      <div className="flex-1">
        <Label htmlFor="add-team-member-uid" className="text-xs">Add a person</Label>
        <PersonPicker
          id="add-team-member-uid"
          orgId={orgId}
          value={userId}
          onChange={setUserId}
          alreadyHasIds={existingUserIds}
          alreadyHasLabel="Already in this team"
        />
      </div>
      <Button type="submit" size="sm" disabled={busy}>Add</Button>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </form>
  )
}
